import React, { useCallback } from 'react';
import DocumentTitle from 'react-document-title';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';
import {
  Table,
  TableHeader,
} from '@fortress-technology-solutions/fortress-component-library/Organisms_Fortress';

import TableLayout from '../../components/TableLayout';
import InitializeApplicableFractionsAction from '../../components/InitializeApplicableFractionsAction';
import useBaseTableProps from '../../hooks/useBaseTableProps';
import useIntl from '../../hooks/useIntl';
import useSelectedProperty from '../../hooks/useSelectedProperty';
import { promptToaster as promptToasterAction } from '../App/actions';
import v1Messages from '../ManageBuildings/messages';

import useManageBuildingsV2 from './hooks';
import SingleBuildingElectionCheckbox from './SingleBuildingElectionCheckbox';
import EditBuildingModal from './EditBuildingModal';
import messages from './messages';

const ManageBuildingsV2 = () => {
  const intl = useIntl();
  const selectedProperty = useSelectedProperty();
  const dispatch = useDispatch();
  const promptToaster = useCallback(
    (toasterPayload: Object) => dispatch(promptToasterAction(toasterPayload)),
    [dispatch],
  );
  const baseTableProps = useBaseTableProps();

  const {
    name,
    gates,
    canEdit,
    buildings,
    isLoading,
    isError,
    count,
    totalCount,
    rows,
    filteredHeaders,
    order,
    orderBy,
    handleSortChange,
    filterState,
    filterTypeState,
    searchState,
    dateState,
    handleFilterChange,
    handleFilterTypeChange,
    handleSearchSubmit,
    handleDateSubmit,
    allColumnsHidden,
    columnOptions,
    selectedColumns,
    handleColumnChange,
    onCSVButtonClick,
    onPDFButtonClick,
    states,
    singleBuildingElection,
    updateSingleBuildingElection,
    editModalProps,
    updateBuilding,
    constructionTypeOptions,
  } = useManageBuildingsV2({
    selectedProperty,
    intl,
    promptToaster,
  });

  const headerActions = [
    ...(gates.isAffordableProperty
      ? [
          <SingleBuildingElectionCheckbox
            key="single-building-election"
            isLIHTCProperty={gates.isLIHTCProperty}
            checked={singleBuildingElection}
            disabled={!canEdit}
            onChange={updateSingleBuildingElection}
            intl={intl}
          />,
        ]
      : []),
    <InitializeApplicableFractionsAction
      key="initialize-applicable-fractions"
      selectedProperty={selectedProperty}
      buildings={buildings}
      intl={intl}
    />,
  ];

  const editedBuilding = editModalProps.modalState.building;

  return (
    <DocumentTitle title={intl.formatMessage(v1Messages.title)}>
      <TableLayout name={name} childrenElementsHeight={50 + 8}>
        {(tableHeight) => (
          <>
            <TableHeader
              title={<FormattedMessage {...v1Messages.header} />}
              count={isLoading || isError ? undefined : count}
              totalCount={totalCount}
              countName={intl.formatMessage(messages.countName)}
              actions={headerActions}
            />
            <Table
              {...baseTableProps}
              name={name}
              height={tableHeight}
              headers={filteredHeaders}
              rows={rows}
              isLoading={isLoading}
              count={count}
              totalCount={totalCount}
              order={order}
              orderBy={orderBy}
              handleSortChange={handleSortChange}
              filterState={filterState}
              filterTypeState={filterTypeState}
              searchState={searchState}
              dateState={dateState}
              handleFilterChange={handleFilterChange}
              handleFilterTypeChange={handleFilterTypeChange}
              handleSearchSubmit={handleSearchSubmit}
              handleDateSubmit={handleDateSubmit}
              allColumnsHidden={allColumnsHidden}
              columnOptions={columnOptions}
              selectedColumns={selectedColumns}
              handleColumnChange={handleColumnChange}
              emptyStateMessage={intl.formatMessage(
                isError ? messages.loadError : messages.emptyState,
              )}
              onCSVButtonClick={count > 0 ? onCSVButtonClick : undefined}
              onPDFButtonClick={count > 0 ? onPDFButtonClick : undefined}
            />
            {editedBuilding && (
              <EditBuildingModal
                open={editModalProps.open}
                building={editedBuilding}
                states={states}
                gates={gates}
                intl={intl}
                constructionTypeOptions={constructionTypeOptions}
                handleClose={editModalProps.handleClose}
                openConfirm={editModalProps.openConfirm}
                openConfirmModal={editModalProps.openConfirmModal}
                closeConfirmModal={editModalProps.closeConfirmModal}
                updateBuilding={updateBuilding}
                promptToaster={promptToaster}
              />
            )}
          </>
        )}
      </TableLayout>
    </DocumentTitle>
  );
};

export default ManageBuildingsV2;
